import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import { powerPin } from './types';
import type { PowerAlert } from './types';

/**
 * A power event in the alerts feed (slice 54) — the SIXTH item shape, beside the
 * geofence crossing, the overspeed episode, the maintenance reminder, the
 * tracker-health event and the after-hours use.
 *
 * TWO TYPES, TWO TONES:
 *   - `power_cut`      (severity `critical`) → external power was severed. This is
 *     the loud one: the attention styling every critical row uses, an error-toned
 *     tag, an accent unread dot.
 *   - `power_restored` (severity `info`)     → external power came back. Good news,
 *     so it is QUIET — the same restraint `tracker_online` and a `minor` overspeed
 *     row show. Never carries attention weight.
 *
 * 🔴 LOCATION IS THE LAST KNOWN FIX, NOT WHERE IT HAPPENED, AND IT MAY NOT EXIST.
 * A powerCut frame carries no trustworthy position of its own (the VL110C freezes
 * its fixtime on a power event), so the pin — when there is one — is the vehicle's
 * last VALID GPS fix, and it is LABELLED "last known location" so it is never read
 * as the spot the cable was pulled. A device that never got a fix has none, and the
 * row must not invent one from live state (ADR 0011 decision 2, position 27). So:
 *   - `power_cut` WITH a pin → a select button (like `OverspeedRow`): selecting it
 *     centres the fleet map on the last-known fix, the same affordance every
 *     positioned row uses. On vehicle detail (no map) selecting just marks it read.
 *   - `power_cut` WITHOUT a pin, and every `power_restored` → a `Link` straight to
 *     the vehicle, the honest destination for a location-less event, exactly as
 *     `TrackerHealthRow` does. No map-select button that would centre on nothing.
 *
 * `powerPin` is the ONLY reader of the wire coordinates, so a rename of the wire
 * fields is a one-line change there, never a hunt across this file.
 *
 * Read/unread is a leading dot + a weight change, never colour alone. Tokens only;
 * no hex literal enters this file.
 */
export function PowerRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: PowerAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: PowerAlert) => void;
  t: TFunction;
}) {
  const isCut = alert.type === 'power_cut';
  // Only a power_cut draws the eye; a restoration is quiet good news, like
  // `tracker_online` and a `minor` overspeed row.
  const isAttention = isCut;
  const pin = isCut ? powerPin(alert) : null;

  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);

  const titleKey = isCut ? 'alerts.power.cut.title' : 'alerts.power.restored.title';
  const tagClass = isAttention
    ? 'bg-raad-error/15 text-raad-error'
    : 'bg-raad-slate/12 text-raad-slate';

  const titleClass =
    isAttention && !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : isAttention ? 'bg-raad-accent' : 'bg-raad-slate';

  // Shared inner content for both wrappers (button when positioned, Link otherwise).
  const inner = (
    <div className="flex items-start gap-2.5">
      <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={['truncate text-sm', titleClass].join(' ')}>{t(titleKey)}</span>
          <span
            data-testid="power-tag"
            className={[
              'flex-none rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              tagClass,
            ].join(' ')}
          >
            {t('alerts.power.tag')}
          </span>
          {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
        </div>

        {plate ? (
          <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
        ) : null}

        <div className="mt-1 text-[12px] text-raad-muted">
          {t('alerts.observedAt', { time: observedTime })}
        </div>

        {/* The last-known location line. Present only for a power_cut: with a pin it
            is the affordance ("selecting this shows the last known location on the
            map"); without one it states, honestly, that there is no fix to show. A
            power_restored carries no location line at all. */}
        {isCut ? (
          pin ? (
            <div
              data-testid="power-last-known"
              className="mt-1 flex items-center gap-1.5 text-[12px] font-semibold text-raad-accent"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11Z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
              {t('alerts.power.lastKnownLocation')}
            </div>
          ) : (
            <div data-testid="power-no-location" className="mt-1 text-[12px] text-raad-dim">
              {t('alerts.power.noLocation')}
            </div>
          )
        ) : null}
      </div>
    </div>
  );

  const containerClass = [
    'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
    selected
      ? 'border-raad-primary/40 bg-raad-primary/10'
      : isAttention
        ? 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15'
        : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
  ].join(' ');

  return (
    <li>
      {pin ? (
        // Positioned: selecting centres the fleet map on the last-known fix (and
        // marks read), exactly like OverspeedRow / AfterHoursRow.
        <button
          type="button"
          onClick={() => onSelect(alert)}
          aria-pressed={selected}
          data-testid="power-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="true"
          className={containerClass}
        >
          {inner}
        </button>
      ) : (
        // Location-less (a restoration, or a cut with no valid fix): a Link to the
        // vehicle — the honest destination — never a map-select button.
        <Link
          to={`/vehicles/${alert.vehicleId}`}
          data-testid="power-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="false"
          className={containerClass}
        >
          {inner}
        </Link>
      )}
    </li>
  );
}
